import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const fmtMoney = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/**
 * Top-N reason buckets (CARC + 277CA). Click a row to set it as a filter.
 * Renders horizontal bar chart with count + dollar amount.
 */
const ReasonRow = ({ entry, maxCount, isActive, onClick, showAmount, dataPrefix }) => {
  const widthPct = maxCount ? Math.max(8, (entry.count / maxCount) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-3 rounded-md border transition-colors ${
        isActive
          ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200'
          : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
      }`}
      data-testid={`${dataPrefix}-${entry.code}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-mono font-semibold text-sm text-slate-900 shrink-0">
            {entry.code}
          </span>
          <span className="text-xs text-slate-600 truncate">{entry.label}</span>
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          <span className="text-sm font-semibold text-slate-900">{entry.count}×</span>
          {showAmount && entry.amount > 0 && (
            <span className="text-xs text-slate-500 font-mono">{fmtMoney(entry.amount)}</span>
          )}
        </div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isActive ? 'bg-indigo-600' : 'bg-indigo-400'
          }`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </button>
  );
};

const ReasonBucketCard = ({ title, badge, entries, activeCode, onSelect, showAmount, dataPrefix }) => {
  const maxCount = entries?.length ? Math.max(...entries.map((e) => e.count)) : 0;
  return (
    <Card className="border-slate-200" data-testid={`${dataPrefix}-card`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{title}</span>
          <span className="text-xs font-normal text-slate-500">{badge}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {!entries || entries.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No data in current filter</p>
        ) : (
          entries.map((e) => (
            <ReasonRow
              key={e.code}
              entry={e}
              maxCount={maxCount}
              isActive={activeCode === e.code}
              onClick={() => onSelect(e.code)}
              showAmount={showAmount}
              dataPrefix={dataPrefix}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Side-by-side cards for top CARC and top 277CA buckets.
 * Click a row to filter the table below by that code.
 */
const TopReasonsPanel = ({ summary, filters, onCarcSelect, on277Select }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    <ReasonBucketCard
      title="Top CARC Reasons (835)"
      badge="Click to filter"
      entries={summary?.top_carc || []}
      activeCode={filters.carc_code}
      onSelect={(code) => onCarcSelect(filters.carc_code === code ? '' : code)}
      showAmount
      dataPrefix="top-carc"
    />
    <ReasonBucketCard
      title="Top 277CA Errors"
      badge="Click to filter"
      entries={summary?.top_277 || []}
      activeCode={filters.error_code}
      onSelect={(code) => on277Select(filters.error_code === code ? '' : code)}
      showAmount={false}
      dataPrefix="top-277"
    />
  </div>
);

export default TopReasonsPanel;
